import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Guest, GuestFormData } from '../../services/guests';
import { GuestCategory } from '../../services/guestCategories';

interface GuestFormProps {
  open: boolean;
  guest: Guest | null;
  categories: GuestCategory[];
  onSave: (data: GuestFormData) => void;
  onDelete?: (guest: Guest) => void;
  onClose: () => void;
}

const DIETARY_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'other', label: 'Other' },
];

const initialForm: GuestFormData = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  num_invited: 1,
  num_expected_to_attend: 1,
  category_level1_id: '',
  category_level2_id: '',
  notes: '',
  dietary_restriction: '',
  dietary_restriction_note: '',
};

const GuestForm: React.FC<GuestFormProps> = ({ open, guest, categories, onSave, onDelete, onClose }) => {
  const [form, setForm] = useState<GuestFormData>(initialForm);

  const level1Cats = categories.filter((c) => c.level === 1);
  const level2Cats = categories.filter((c) => c.level === 2);

  useEffect(() => {
    if (guest) {
      setForm({
        first_name: guest.first_name,
        last_name: guest.last_name,
        phone: guest.phone || '',
        email: guest.email || '',
        num_invited: guest.num_invited,
        num_expected_to_attend: guest.num_expected_to_attend,
        category_level1_id: guest.category_level1_id || '',
        category_level2_id: guest.category_level2_id || '',
        notes: guest.notes || '',
        dietary_restriction: guest.dietary_restriction || '',
        dietary_restriction_note: guest.dietary_restriction_note || '',
      });
    } else {
      setForm(initialForm);
    }
  }, [guest, open]);

  const handleChange = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const expectedExceedsInvited = (form.num_expected_to_attend ?? 0) > (form.num_invited ?? 1);

  const handleSubmit = () => {
    if (expectedExceedsInvited) return;
    onSave({
      ...form,
      phone: form.phone || null,
      email: form.email || null,
      category_level1_id: form.category_level1_id || null,
      category_level2_id: form.category_level2_id || null,
      notes: form.notes || null,
      dietary_restriction: form.dietary_restriction || null,
      dietary_restriction_note: form.dietary_restriction === 'other' ? (form.dietary_restriction_note || null) : null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{guest ? 'Edit Guest' : 'New Guest'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={6}>
            <TextField
              label="First Name"
              fullWidth
              required
              value={form.first_name}
              onChange={(e) => handleChange('first_name', e.target.value)}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Last Name"
              fullWidth
              required
              value={form.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Phone"
              fullWidth
              value={form.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Email"
              fullWidth
              type="email"
              value={form.email}
              onChange={(e) => handleChange('email', e.target.value)}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Number Invited"
              fullWidth
              type="number"
              value={form.num_invited}
              onChange={(e) => handleChange('num_invited', parseInt(e.target.value) || 1)}
              slotProps={{ htmlInput: { min: 1 } }}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Expected to Attend"
              fullWidth
              type="number"
              value={form.num_expected_to_attend}
              onChange={(e) => handleChange('num_expected_to_attend', parseInt(e.target.value) || 0)}
              slotProps={{ htmlInput: { min: 0 } }}
              error={expectedExceedsInvited}
              helperText={expectedExceedsInvited ? 'Cannot exceed number invited' : ''}
            />
          </Grid>
          <Grid size={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={form.category_level1_id}
                label="Category"
                onChange={(e) => handleChange('category_level1_id', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {level1Cats.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Sub Category</InputLabel>
              <Select
                value={form.category_level2_id}
                label="Sub Category"
                onChange={(e) => handleChange('category_level2_id', e.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {level2Cats.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Dietary Restriction</InputLabel>
              <Select
                value={form.dietary_restriction}
                label="Dietary Restriction"
                onChange={(e) => handleChange('dietary_restriction', e.target.value)}
              >
                {DIETARY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {form.dietary_restriction === 'other' && (
            <Grid size={6}>
              <TextField
                label="Specify Restriction"
                fullWidth
                value={form.dietary_restriction_note}
                onChange={(e) => handleChange('dietary_restriction_note', e.target.value)}
              />
            </Grid>
          )}
          <Grid size={12}>
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={2}
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: guest && onDelete ? 'space-between' : 'flex-end' }}>
        {guest && onDelete && (
          <Button
            onClick={() => onDelete(guest)}
            sx={{ color: 'error.main' }}
          >
            Delete
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} sx={{ color: 'grey.600' }}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!form.first_name.trim() || !form.last_name.trim() || expectedExceedsInvited}
          >
            {guest ? 'Save' : 'Create'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default GuestForm;
